import { getCache, setCache } from './cacheUtils';
import SunCalculator from '../SunCalculator';

/**
 * Clean a time string from Al Adhan API.
 * The API sometimes returns times with timezone suffix like "05:40 (AEDT)".
 * This strips everything after the HH:MM portion.
 */
const cleanTimeString = (str) => {
  if (!str) return null;
  const match = str.match(/^(\d{1,2}:\d{2})/);
  return match ? match[1] : null;
};

/**
 * Al Adhan API calculation methods:
 *  0  Shia Ithna-Ansari
 *  1  University of Islamic Sciences, Karachi
 *  2  Islamic Society of North America (ISNA)        — Fajr 15°, Isha 15°
 *  3  Muslim World League (MWL)                      — Fajr 18°, Isha 17°
 *  4  Umm Al-Qura University, Makkah                 — Fajr 18.5°, Isha 90min
 *  5  Egyptian General Authority of Survey            — Fajr 19.5°, Isha 17.5°
 *  7  Institute of Geophysics, University of Tehran
 *  8  Gulf Region
 *  9  Kuwait
 * 11  Majlis Ugama Islam Singapura
 * 12  Union Organization islamique de France
 * 13  Diyanet İşleri Başkanlığı, Turkey
 * 14  Spiritual Administration of Muslims of Russia
 * 15  Moonsighting Committee Worldwide
 * 20  Kementerian Agama Republik Indonesia (KEMENAG)  — Fajr 20°, Isha 18°
 *
 * Auto-detect the best method based on geographic region.
 */
const getMethodForLocation = (lat, lng) => {
  // Check localStorage for user override first
  const saved = localStorage.getItem('sunriseApp.prayerMethod');
  if (saved) return parseInt(saved, 10);

  // Turkey (check before broader Europe/Middle East)
  if (lng >= 25 && lng <= 45 && lat >= 36 && lat <= 42) return 13;

  // North America
  if (lng >= -170 && lng <= -50 && lat >= 10 && lat <= 75) return 2;

  // Europe (excluding Turkey)
  if (lng >= -15 && lng <= 45 && lat >= 35 && lat <= 72) return 3;

  // Arabian Peninsula (Saudi, UAE, Qatar, Kuwait, Bahrain, Oman, Yemen)
  if (lng >= 34 && lng <= 60 && lat >= 12 && lat <= 32) return 4;

  // Egypt & North Africa
  if (lng >= -20 && lng <= 35 && lat >= 15 && lat <= 37) return 5;

  // Pakistan, India, Bangladesh, Afghanistan
  if (lng >= 60 && lng <= 95 && lat >= 5 && lat <= 40) return 1;

  // Singapore, Malaysia
  if (lng >= 95 && lng <= 120 && lat >= -10 && lat <= 8) return 11;

  // Indonesia, Australia, New Zealand, Oceania
  if (lng >= 90 && lng <= 180 && lat >= -50 && lat <= 15) return 20;

  // Russia, Central Asia
  if (lat >= 40 && lat <= 82 && lng >= 40 && lng <= 180) return 14;

  // South America, Africa (sub-Saharan), other
  return 3; // MWL as sensible global default
};

/**
 * Fetch Islamic prayer times from Al Adhan API
 * @param {number} lat - Latitude coordinate
 * @param {number} lng - Longitude coordinate
 * @param {Date} date - Date to fetch prayer times for (defaults to current date)
 * @returns {Promise<Object>} Prayer times for the specified date and location
 */
export const fetchPrayerTimes = async (lat, lng, date = new Date()) => {
  // Format date for cache key (YYYY-MM-DD for internal use)
  const dateStrInternal = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;

  // Format date for Al Adhan API (DD-MM-YYYY as required by the API)
  const dateStrApi = `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;

  // Auto-detect best calculation method for this location
  const method = getMethodForLocation(lat, lng);

  // Cache key includes method so changing method invalidates cache
  const cacheKey = `prayerApp.api.${lat.toFixed(4)},${lng.toFixed(4)}.${dateStrInternal}.m${method}`;

  // Check cache first
  const cachedData = getCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  try {
    const response = await fetch(
      `https://api.aladhan.com/v1/timings/${dateStrApi}?latitude=${lat}&longitude=${lng}&method=${method}`
    );

    if (!response.ok) {
      throw new Error(`Prayer API responded with status: ${response.status}`);
    }

    const data = await response.json();

    if (data.code === 200) {
      const prayerTimes = {
        fajr: cleanTimeString(data.data.timings.Fajr) || data.data.timings.Fajr,
        sunrise: cleanTimeString(data.data.timings.Sunrise) || data.data.timings.Sunrise,
        dhuhr: cleanTimeString(data.data.timings.Dhuhr) || data.data.timings.Dhuhr,
        asr: cleanTimeString(data.data.timings.Asr) || data.data.timings.Asr,
        maghrib: cleanTimeString(data.data.timings.Maghrib) || data.data.timings.Maghrib,
        isha: cleanTimeString(data.data.timings.Isha) || data.data.timings.Isha,
        method: data.data.meta.method.name,
        date: data.data.date.gregorian.date,
        timezone: data.data.meta.timezone || null
      };

      // Cache the result
      setCache(cacheKey, prayerTimes);

      return prayerTimes;
    } else {
      throw new Error('Prayer API response was not OK');
    }
  } catch (error) {
    console.error('Error fetching Islamic prayer times:', error);

    // Try stale cache before falling back to calculation
    const staleData = getCache(cacheKey, true);
    if (staleData) {
      return staleData;
    }

    // Generate approximate prayer times from SunCalculator
    const mockPrayerTimes = generateFallbackPrayerTimes(lat, lng, date, dateStrInternal);
    setCache(cacheKey, mockPrayerTimes);
    return mockPrayerTimes;
  }
};

/**
 * Generate approximate prayer times from SunCalculator when API is unavailable
 */
const generateFallbackPrayerTimes = (lat, lng, date, dateStr) => {
  try {
    const calcTimes = SunCalculator.calculateTimes(date, lat, lng);
    if (calcTimes.sunrise && calcTimes.sunset) {
      // SunCalculator returns correct UTC timestamps.  To display times in
      // the location's timezone we estimate the UTC offset from longitude
      // (~15° per hour).  This is approximate (±1 h) but acceptable for an
      // offline fallback.
      const estOffsetMin = Math.round(lng / 15) * 60;
      const toHHMM = (d) => {
        const utcMin = d.getUTCHours() * 60 + d.getUTCMinutes();
        const locMin = ((utcMin + estOffsetMin) % 1440 + 1440) % 1440;
        const h = Math.floor(locMin / 60);
        const m = locMin % 60;
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      };
      const midday = new Date(calcTimes.sunrise.getTime() + (calcTimes.sunset.getTime() - calcTimes.sunrise.getTime()) / 2);
      return {
        fajr: toHHMM(new Date(calcTimes.sunrise.getTime() - 90 * 60000)),
        sunrise: toHHMM(calcTimes.sunrise),
        dhuhr: toHHMM(midday),
        asr: toHHMM(new Date(calcTimes.sunset.getTime() - 2 * 3600000)),
        maghrib: toHHMM(calcTimes.sunset),
        isha: toHHMM(new Date(calcTimes.sunset.getTime() + 90 * 60000)),
        method: "Calculated (offline)",
        date: dateStr,
        timezone: null
      };
    }
  } catch (e) {
    // Fall through to hardcoded fallback
  }
  return {
    fajr: "05:30",
    sunrise: "06:45",
    dhuhr: "12:15",
    asr: "15:30",
    maghrib: "18:15",
    isha: "19:45",
    method: "Fallback (offline)",
    date: dateStr,
    timezone: null
  };
};
